/**
 * VaultPickerModal — browse the Vault and pick ONE node (a folder or a file).
 *
 * Shared by the agent editor and the skill editor, so "Add from the Vault" is
 * the same gesture wherever it appears. It shows exactly what the person can
 * reach: `/vault/folders` returns the roots they have access to, and each
 * folder is expanded on demand.
 *
 * Picking a FOLDER is the interesting case — the folder keeps its own contents,
 * so files added to it later reach the agent without anyone re-attaching them.
 * That is the whole point of linking to the Vault instead of uploading a copy.
 */

import { useCallback, useEffect, useState } from 'react';
import { Modal, Tree, Spin, Empty, Checkbox, message } from 'antd';
import type { DataNode } from 'antd/es/tree';
import { useLanguage } from '@/hooks/useLanguage';
import { vaultService } from '../vaultService';
import type { VaultFolder } from '../types';

export interface VaultPick {
  folder_id?: string;
  file_id?: string;
  name: string;
  include_as_context: boolean;
}

interface Props {
  open: boolean;
  /** Nodes already attached — shown but not selectable, so nobody links twice. */
  attached?: Set<string>;
  /** Only folders are pickable (files stay visible for orientation, greyed).
   *  Also hides the include-as-context checkbox — the AoA config attaches
   *  whole folders and has no per-link context flag. */
  foldersOnly?: boolean;
  onPick: (pick: VaultPick) => Promise<void> | void;
  onCancel: () => void;
}

const FolderGlyph = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round">
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  </svg>
);

const FileGlyph = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round">
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
  </svg>
);

interface PickerNode extends DataNode {
  ref: { folder_id?: string; file_id?: string };
  name: string;
}

export function VaultPickerModal({ open, attached, foldersOnly, onPick, onCancel }: Props) {
  const { t } = useLanguage();
  const [nodes, setNodes] = useState<PickerNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<PickerNode | null>(null);
  const [asContext, setAsContext] = useState(false);
  const [saving, setSaving] = useState(false);

  const isAttached = useCallback(
    (id?: string) => !!id && !!attached?.has(id),
    [attached],
  );

  const folderNode = useCallback(
    (f: VaultFolder): PickerNode => ({
      key: `folder:${f.folder_id}`,
      ref: { folder_id: f.folder_id },
      name: f.folder_name,
      disabled: isAttached(f.folder_id),
      title: (
        <span className="vault-pick-node">
          <FolderGlyph />
          <span className="vault-pick-label" dir="auto">{f.folder_name}</span>
          {f.file_count > 0 && (
            <span className="vault-pick-meta">{t('vault.picker.fileCount', { count: f.file_count })}</span>
          )}
          {isAttached(f.folder_id) && <span className="vault-pick-attached">{t('vault.picker.attached')}</span>}
        </span>
      ) as unknown as string,
      isLeaf: false,
    }),
    [isAttached, t],
  );

  const fileNode = useCallback(
    (folder: VaultFolder, fileId: string, name: string): PickerNode => ({
      key: `file:${folder.folder_id}:${fileId}`,
      ref: { file_id: fileId },
      name,
      disabled: foldersOnly || isAttached(fileId),
      title: (
        <span className="vault-pick-node">
          <FileGlyph />
          <span dir="auto" className="vault-pick-label">{name}</span>
          {isAttached(fileId) && <span className="vault-pick-attached">{t('vault.picker.attached')}</span>}
        </span>
      ) as unknown as string,
      isLeaf: true,
    }),
    [isAttached, foldersOnly, t],
  );

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSelected(null);
    vaultService
      .listRootFolders()
      .then((roots) => setNodes(roots.map(folderNode)))
      .catch((e) => message.error(e?.response?.data?.error || t('vault.loadFailed')))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const patch = useCallback(
    (list: PickerNode[], key: React.Key, children: PickerNode[]): PickerNode[] =>
      list.map((n) => {
        if (n.key === key) return { ...n, children };
        if (n.children) return { ...n, children: patch(n.children as PickerNode[], key, children) };
        return n;
      }),
    [],
  );

  /** Subfolders first, then the folder's own files — Drive ordering. */
  const loadChildren = useCallback(
    async (node: PickerNode) => {
      if (!node.ref.folder_id) return;
      const folder = await vaultService.getFolder(node.ref.folder_id);
      const kids: PickerNode[] = [
        ...(folder.children || []).map(folderNode),
        ...(folder.files || []).map((f) =>
          fileNode(folder, f.file_id, f.file_display_name || f.file_name || t('vault.picker.aFile')),
        ),
      ];
      setNodes((prev) => patch(prev, node.key, kids));
    },
    [folderNode, fileNode, patch, t],
  );

  const confirm = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await onPick({ ...selected.ref, name: selected.name, include_as_context: asContext });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t('vault.picker.title')}
      onCancel={onCancel}
      onOk={confirm}
      okText={t('vault.picker.attach')}
      okButtonProps={{ disabled: !selected, loading: saving }}
      width={560}
      destroyOnHidden
    >
      <p className="vault-pick-hint">{t('vault.picker.hint')}</p>
      <div className="vault-pick-tree">
        {loading ? (
          <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>
        ) : nodes.length === 0 ? (
          <Empty description={t('vault.picker.empty')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <Tree
            treeData={nodes as unknown as DataNode[]}
            loadData={loadChildren as any}
            selectedKeys={selected ? [selected.key] : []}
            onSelect={(_keys, info) => setSelected(info.selected ? (info.node as unknown as PickerNode) : null)}
            blockNode
          />
        )}
      </div>
      {/* Read-only on purpose: agents cannot write to the Vault (the kill switch
          in agent_conversation.py withholds the edit tool), so offering an
          "edit" mode here would promise something that never happens. */}
      {!foldersOnly && (
        <div style={{ marginBlockStart: 14 }}>
          <Checkbox checked={asContext} onChange={(e) => setAsContext(e.target.checked)}>
            {t('vault.picker.alwaysInContext')}
          </Checkbox>
          <div className="vault-pick-hint" style={{ marginBlockStart: 4 }}>
            {t('vault.picker.contextHint')}
          </div>
        </div>
      )}
    </Modal>
  );
}
